import React, { useState, useEffect, useRef } from 'react';
import { Send, Mail, User, MessageSquare, Phone, Sparkles, CheckCircle, Star, Heart, ArrowRight } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [gradientAngle, setGradientAngle] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [animationPhase, setAnimationPhase] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);
  const containerRef = useRef(null);

  // EmailJS credentials - REPLACE THESE WITH YOUR ACTUAL CREDENTIALS
  const EMAILJS_CONFIG = {
    PUBLIC_KEY: '9K_KsiIbIVZpXoTd6',
    SERVICE_ID: 'service_gliflzp',
    TEMPLATE_ID: 'template_550dnfy' // You need to create a template and get its ID
  };

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Generate floating elements
  const generateFloatingElements = (count, mobile) => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * (mobile ? 15 : 30) + 10,
      delay: Math.random() * 5,
      duration: Math.random() * 20 + 15,
      type: i % 5,
      rotation: Math.random() * 360
    }));
  };

  const [floatingElements, setFloatingElements] = useState([]);

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isMobile) return;
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 30 - 15,
        y: (e.clientY / window.innerHeight) * 30 - 15
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [isMobile]);

  // Scroll progress effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const heroHeight = containerRef.current?.clientHeight || 800;
      const progress = (scrollTop / heroHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Animated gradient colors
  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle((prev) => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Animation phase for wave effect
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 0.5) % 360);
    }, 30);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setFloatingElements(generateFloatingElements(isMobile ? 25 : 40, isMobile));
  }, [isMobile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing again
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Initialize EmailJS with your public key
      emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
      
      // Prepare template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone || 'Not provided',
        message: formData.message,
        to_name: 'Fathima Zahara',
        to_email: 'fathimazahara@example.com', // Change this to your email
        reply_to: formData.email,
        date: new Date().toLocaleString()
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID, // You need to create a template and get its ID
        templateParams
      );

      console.log('Email sent successfully:', result);
      
      // Show success message
      setIsSubmitted(true);
      setIsLoading(false);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }, 3000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Failed to send message. Please try again or contact me directly.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <section 
        ref={containerRef}
        id="contact" 
        className="min-h-screen relative overflow-hidden bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8"
        style={{
          '--gradient-angle': `${gradientAngle}deg`,
          '--animation-phase': `${animationPhase}deg`,
          background: !isMobile ? `radial-gradient(circle at ${mousePosition.x + 50}% ${mousePosition.y + 50}%, rgba(255, 182, 193, 0.3) 0%, transparent 60%),
                      radial-gradient(circle at ${100 - mousePosition.x + 50}% ${100 - mousePosition.y + 50}%, rgba(251, 207, 232, 0.35) 0%, transparent 60%),
                      linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.95) 0%, rgba(255, 228, 230, 0.85) 50%, rgba(255, 240, 245, 0.95) 100%)` :
                      `linear-gradient(var(--gradient-angle), rgba(255, 240, 245, 0.95) 0%, rgba(255, 228, 230, 0.85) 50%, rgba(255, 240, 245, 0.95) 100%)`
        }}
      >
        {/* Dynamic background grid */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255, 182, 193, 0.15) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255, 182, 193, 0.15) 1px, transparent 1px)`,
            backgroundSize: isMobile ? '30px 30px' : '50px 50px',
            maskImage: 'radial-gradient(circle at center, black 30%, transparent 70%)'
          }} />
        </div>

        {/* Animated wave background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-pink-200/10 via-transparent to-transparent animate-wave"></div>
        </div>

        {/* Floating Bubbles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 0).map((bubble) => (
            <div
              key={`bubble-${bubble.id}`}
              className="absolute rounded-full animate-bubble-float"
              style={{
                left: `${bubble.x}%`,
                top: `${bubble.y}%`,
                width: `${bubble.size}px`,
                height: `${bubble.size}px`,
                background: `radial-gradient(circle at 30% 30%, 
                  rgba(255, 255, 255, 0.9) 0%, 
                  rgba(255, 255, 255, 0.7) 30%,
                  rgba(255, 200, 221, 0.4) 70%,
                  transparent 100%
                )`,
                border: '1px solid rgba(255, 255, 255, 0.5)',
                animationDelay: `${bubble.delay}s`,
                animationDuration: `${bubble.duration}s`,
                filter: 'blur(0.5px)',
                boxShadow: 'inset 0 0 15px rgba(255, 255, 255, 0.6), 0 0 25px rgba(255, 182, 193, 0.4)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Floating Stars */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 1).map((star) => (
            <div
              key={`star-${star.id}`}
              className="absolute animate-star-twinkle"
              style={{
                left: `${star.x}%`,
                top: `${star.y}%`,
                width: `${star.size / 2}px`,
                height: `${star.size / 2}px`,
                background: `radial-gradient(circle, 
                  rgba(255, 255, 255, 0.95) 0%,
                  rgba(255, 255, 255, 0.8) 30%,
                  rgba(255, 182, 193, 0.6) 70%,
                  transparent 100%
                )`,
                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
                filter: 'drop-shadow(0 0 3px rgba(255, 182, 193, 0.6))',
                willChange: 'transform, opacity, filter'
              }}
            />
          ))}
        </div>

        {/* Floating Hearts */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.filter(el => el.type === 2).map((heart) => (
            <div
              key={`heart-${heart.id}`}
              className="absolute animate-heart-float"
              style={{
                left: `${heart.x}%`,
                top: `${heart.y}%`,
                width: `${heart.size / 2}px`,
                height: `${heart.size / 2}px`,
                background: `linear-gradient(135deg, 
                  rgba(255, 100, 150, 0.3) 0%,
                  rgba(255, 150, 200, 0.2) 50%,
                  rgba(255, 100, 150, 0.1) 100%
                )`,
                clipPath: 'polygon(50% 0%, 100% 35%, 82% 100%, 50% 75%, 18% 100%, 0% 35%)',
                animationDelay: `${heart.delay}s`,
                animationDuration: `${heart.duration}s`,
                filter: 'blur(1px)',
                willChange: 'transform, opacity'
              }}
            />
          ))}
        </div>

        {/* Animated gradient orbs - Hide on mobile */}
        {!isMobile && (
          <>
            <div 
              className="absolute top-20 left-10 w-32 md:w-48 h-32 md:h-48 bg-gradient-to-br from-pink-400/25 via-rose-400/25 to-purple-400/15 rounded-full mix-blend-multiply filter blur-2xl md:blur-3xl opacity-40 animate-orb-float"
              style={{
                transform: `translate(${mousePosition.x * 0.4}px, ${mousePosition.y * 0.4}px) rotate(${mousePosition.x * 0.5}deg)`,
                animationDelay: '0.5s'
              }}
            />
            
            <div 
              className="absolute bottom-32 right-10 w-40 md:w-56 h-40 md:h-56 bg-gradient-to-tr from-purple-300/30 via-pink-300/30 to-rose-300/20 rounded-full mix-blend-multiply filter blur-2xl md:blur-3xl opacity-30 animate-orb-float-slow"
              style={{
                transform: `translate(${-mousePosition.x * 0.3}px, ${-mousePosition.y * 0.3}px) rotate(${-mousePosition.x * 0.3}deg)`,
                animationDelay: '1s'
              }}
            />
          </>
        )}

        {/* Animated decorative icons with trails - Adjust for mobile */}
        <div className="absolute top-8 sm:top-10 right-4 sm:right-10 group cursor-pointer">
          <div className="absolute inset-0 bg-pink-400/10 rounded-full blur-lg sm:blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
          <Sparkles 
            className="relative text-pink-400/60 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 animate-spin-slow group-hover:text-pink-500 group-hover:scale-110 transition-all duration-500"
            style={!isMobile ? { transform: `translate(${mousePosition.x * 0.1}px, ${mousePosition.y * 0.1}px)` } : {}}
          />
        </div>
        
        <div className="absolute bottom-20 sm:bottom-32 left-4 sm:left-10 group cursor-pointer">
          <div className="absolute inset-0 bg-rose-400/10 rounded-full blur-lg sm:blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
          <Heart 
            className="relative text-rose-400/70 w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8 animate-pulse group-hover:text-rose-500 group-hover:scale-110 transition-all duration-500"
            style={!isMobile ? { transform: `translate(${mousePosition.x * 0.2}px, ${mousePosition.y * 0.2}px)` } : {}}
          />
        </div>
        
        {!isMobile && (
          <div className="absolute top-32 left-1/3 group cursor-pointer">
            <div className="absolute inset-0 bg-purple-500/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>
            <Star 
              className="relative text-purple-500/50 w-7 h-7 md:w-9 md:h-9 animate-spin group-hover:text-purple-600 group-hover:scale-110 transition-all duration-500"
              style={{ transform: `translate(${mousePosition.x * 0.15}px, ${mousePosition.y * 0.15}px)` }}
            />
          </div>
        )}

        {/* Scroll progress indicator with glow effect */}
        <div className="fixed top-0 left-0 right-0 h-1 z-50">
          <div 
            className="h-full bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 transition-all duration-300 shadow-lg"
            style={{ width: `${scrollProgress}%` }}
          />
          <div 
            className="absolute top-0 h-full w-full bg-gradient-to-r from-pink-400/50 via-rose-400/50 to-purple-400/50 blur-lg transition-all duration-300"
            style={{ width: `${scrollProgress}%` }}
          />
        </div>

        <div className="max-w-3xl mx-auto relative z-10">
          {/* Header with enhanced animation */}
          <div className="text-center mb-8 sm:mb-10 md:mb-12 relative animate-slide-up-stagger">
            <div className="inline-flex items-center gap-1 sm:gap-2 md:gap-3 mb-3 sm:mb-4 relative">
              {/* Animated border */}
              <div className="absolute -inset-1 sm:-inset-2 md:-inset-3 bg-gradient-to-r from-pink-400 via-rose-400 to-purple-400 animate-gradient-x rounded-full blur-md sm:blur-lg opacity-30"></div>
              
              <div className="w-4 sm:w-6 md:w-8 lg:w-12 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent"></div>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-pink-500 animate-pulse relative" />
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 tracking-tight relative px-1 sm:px-2">
                Get In <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 bg-clip-text text-transparent animate-gradient-flow">Touch</span>
                <span className="absolute -bottom-1 left-0 right-0 h-px bg-gradient-to-r from-transparent via-pink-400 to-transparent animate-width-pulse"></span>
              </h1>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-pink-500 animate-pulse relative" />
              <div className="w-4 sm:w-6 md:w-8 lg:w-12 h-px bg-gradient-to-l from-transparent via-pink-400 to-transparent"></div>
            </div>
            
            <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-2xl mx-auto animate-fade-in-stagger mt-4 sm:mt-6 relative px-2 sm:px-4" style={{ animationDelay: '200ms' }}>
              <span className="relative inline-block">
                Have questions or want to connect? I'd love to hear from you!
                <div className="absolute -bottom-1 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-pink-300 to-transparent animate-width-pulse-slow"></div>
              </span>
            </p>
          </div>

          {/* Contact Form Container */}
          <div className="bg-white/90 backdrop-blur-lg sm:backdrop-blur-xl rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-xl sm:shadow-2xl overflow-hidden border border-pink-100 hover:shadow-2xl sm:hover:shadow-3xl transition-all duration-500 animate-fade-in-stagger" style={{ animationDelay: '300ms' }}>
            
            {/* Form Header */}
            <div className="p-4 sm:p-6 md:p-8 border-b border-pink-100 bg-gradient-to-r from-pink-50 via-rose-50 to-purple-50 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              <div className="flex items-center gap-2 sm:gap-3 relative">
                <div className="p-2 sm:p-3 bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 rounded-lg sm:rounded-xl lg:rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-500 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <Send className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-white relative" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 group-hover:scale-105 transition-transform duration-500">Send a Message</h2>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base">I'll respond within 24 hours</p>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="p-3 sm:p-4 md:p-6 lg:p-8">
              {isSubmitted ? (
                <div className="text-center py-6 sm:py-8 md:py-10 lg:py-12 animate-scale-in">
                  <div className="relative inline-block mb-3 sm:mb-4 md:mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-xl sm:blur-2xl opacity-50 animate-success-pulse"></div>
                    <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 rounded-full flex items-center justify-center relative animate-success-pulse">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 lg:w-10 lg:h-10 text-white relative" />
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2 group-hover:scale-105 transition-transform duration-500">
                    Message Sent!
                  </h3>
                  <p className="text-gray-600 text-sm sm:text-base md:text-lg px-2 sm:px-4">
                    Thank you for reaching out. I've received your message and will get back to you soon.
                  </p>
                  
                  {/* Success animation elements */}
                  <div className="flex justify-center gap-1 sm:gap-2 mt-3 sm:mt-4">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.1}s` }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6 lg:space-y-8">
                  
                  {/* Error message display */}
                  {error && (
                    <div className="p-3 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg sm:rounded-xl animate-fade-in-stagger">
                      <div className="flex items-center gap-2 text-red-600">
                        <div className="w-4 h-4 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs">!</span>
                        </div>
                        <p className="text-xs sm:text-sm">{error}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Name and Email Fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                    {/* Name Field */}
                    <div className="relative animate-fade-in-stagger group/field" style={{ animationDelay: '400ms' }}>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <User className="w-3 h-3 sm:w-4 sm:h-4 text-rose-500 animate-pulse-slow" />
                          Full Name *
                        </div>
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-rose-500/0 to-purple-500/0 rounded-lg sm:rounded-xl group-hover:from-pink-500/10 group-hover:via-rose-500/10 group-hover:to-purple-500/10 transition-all duration-500"></div>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          onFocus={() => setActiveField('name')}
                          onBlur={() => setActiveField(null)}
                          required
                          className={`w-full px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 border-2 ${activeField === 'name' ? 'border-rose-400 shadow-lg' : 'border-pink-100'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all duration-500 bg-white/50 backdrop-blur-sm group-hover:border-pink-300 relative text-sm sm:text-base`}
                          placeholder="Your name"
                        />
                        {formData.name && (
                          <div className="absolute right-2 sm:right-3 top-2 sm:top-3 md:top-4 group/check">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-sm opacity-50"></div>
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-emerald-500 animate-checkmark relative" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="relative animate-fade-in-stagger group/field" style={{ animationDelay: '500ms' }}>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        <div className="flex items-center gap-1 sm:gap-2">
                          <Mail className="w-3 h-3 sm:w-4 sm:h-4 text-rose-500 animate-pulse-slow" />
                          Email Address *
                        </div>
                      </label>
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-rose-500/0 to-purple-500/0 rounded-lg sm:rounded-xl group-hover:from-pink-500/10 group-hover:via-rose-500/10 group-hover:to-purple-500/10 transition-all duration-500"></div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          onFocus={() => setActiveField('email')}
                          onBlur={() => setActiveField(null)}
                          required
                          className={`w-full px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 border-2 ${activeField === 'email' ? 'border-rose-400 shadow-lg' : 'border-pink-100'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all duration-500 bg-white/50 backdrop-blur-sm group-hover:border-pink-300 relative text-sm sm:text-base`}
                          placeholder="you@example.com"
                        />
                        {formData.email && (
                          <div className="absolute right-2 sm:right-3 top-2 sm:top-3 md:top-4 group/check">
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-sm opacity-50"></div>
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-emerald-500 animate-checkmark relative" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Phone Field - Full width on mobile, half on larger screens */}
                  <div className="relative animate-fade-in-stagger group/field" style={{ animationDelay: '600ms' }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-pink-500 animate-pulse-slow" />
                        Phone Number
                      </div>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-rose-500/0 to-purple-500/0 rounded-lg sm:rounded-xl group-hover:from-pink-500/10 group-hover:via-rose-500/10 group-hover:to-purple-500/10 transition-all duration-500"></div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onFocus={() => setActiveField('phone')}
                        onBlur={() => setActiveField(null)}
                        className={`w-full px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 border-2 ${activeField === 'phone' ? 'border-pink-300 shadow-lg' : 'border-pink-100'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 transition-all duration-500 bg-white/50 backdrop-blur-sm group-hover:border-pink-300 relative text-sm sm:text-base`}
                        placeholder="+971 (123) 456-7890"
                      />
                      {formData.phone && (
                        <div className="absolute right-2 sm:right-3 top-2 sm:top-3 md:top-4 group/check">
                          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-sm opacity-50"></div>
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-emerald-500 animate-checkmark relative" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Message Field */}
                  <div className="relative animate-fade-in-stagger group/field" style={{ animationDelay: '700ms' }}>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-rose-500 animate-pulse-slow" />
                        Your Message *
                      </div>
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/0 via-rose-500/0 to-purple-500/0 rounded-lg sm:rounded-xl group-hover:from-pink-500/10 group-hover:via-rose-500/10 group-hover:to-purple-500/10 transition-all duration-500"></div>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        onFocus={() => setActiveField('message')}
                        onBlur={() => setActiveField(null)}
                        required
                        rows="3"
                        className={`w-full px-3 sm:px-4 md:px-5 py-2 sm:py-3 md:py-4 border-2 ${activeField === 'message' ? 'border-rose-400 shadow-lg' : 'border-pink-100'} rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-rose-300 transition-all duration-500 bg-white/50 backdrop-blur-sm resize-none group-hover:border-pink-300 relative text-sm sm:text-base`}
                        placeholder="How can I help you?"
                      />
                      {formData.message && (
                        <div className="absolute right-2 sm:right-3 top-2 sm:top-3 group/check">
                          <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full blur-sm opacity-50"></div>
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-emerald-500 animate-checkmark relative" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2 sm:pt-3 md:pt-4 animate-fade-in-stagger" style={{ animationDelay: '800ms' }}>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className={`w-full py-2 sm:py-3 md:py-4 rounded-lg sm:rounded-xl font-semibold transition-all duration-500 transform hover:scale-[1.02] active:scale-95 shadow-lg group relative overflow-hidden animate-button-pulse ${
                        isLoading
                          ? 'bg-gradient-to-r from-gray-400 to-gray-500 text-gray-200 cursor-not-allowed'
                          : 'bg-gradient-to-r from-rose-500 via-pink-500 to-purple-500 text-white hover:shadow-xl hover:shadow-rose-300/30'
                      }`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      {isLoading ? (
                        <div className="flex items-center justify-center gap-1 sm:gap-2 md:gap-3 relative">
                          <div className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span className="text-xs sm:text-sm md:text-base">Sending...</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1 sm:gap-2 md:gap-3 group-hover:gap-2 sm:group-hover:gap-3 md:group-hover:gap-4 transition-all duration-500 relative">
                          <Send className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                          <span className="text-xs sm:text-sm md:text-base">Send Message</span>
                          <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-500" />
                        </div>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced CSS Animations */}
      <style jsx>{`
        @keyframes bubble-float {
          0%, 100% {
            transform: translateY(0) translateX(0) scale(1) rotate(0deg);
            opacity: 0.3;
          }
          25% {
            transform: translateY(-40px) translateX(15px) scale(1.15) rotate(5deg);
            opacity: 0.7;
          }
          50% {
            transform: translateY(-70px) translateX(-10px) scale(1) rotate(0deg);
            opacity: 0.4;
          }
          75% {
            transform: translateY(-25px) translateX(20px) scale(1.1) rotate(-5deg);
            opacity: 0.6;
          }
        }
        
        @keyframes star-twinkle {
          0%, 100% {
            opacity: 0.3;
            transform: scale(1) rotate(0deg);
            filter: drop-shadow(0 0 2px rgba(255, 182, 193, 0.6));
          }
          25% {
            opacity: 0.9;
            transform: scale(1.3) rotate(90deg);
            filter: drop-shadow(0 0 5px rgba(255, 182, 193, 0.9));
          }
          50% {
            opacity: 0.4;
            transform: scale(0.95) rotate(180deg);
            filter: drop-shadow(0 0 3px rgba(255, 182, 193, 0.7));
          }
          75% {
            opacity: 0.8;
            transform: scale(1.2) rotate(270deg);
            filter: drop-shadow(0 0 4px rgba(255, 182, 193, 0.8));
          }
        }
        
        @keyframes heart-float {
          0%, 100% {
            transform: translateY(0) scale(1) rotate(0deg);
            opacity: 0.2;
          }
          25% {
            transform: translateY(-50px) scale(1.2) rotate(45deg);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-90px) scale(0.9) rotate(90deg);
            opacity: 0.3;
          }
          75% {
            transform: translateY(-35px) scale(1.1) rotate(135deg);
            opacity: 0.5;
          }
        }
        
        @keyframes orb-float {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          50% {
            transform: translateY(-25px) rotate(-3deg) scale(1.05);
          }
        }
        
        @keyframes orb-float-slow {
          0%, 100% {
            transform: translateY(0) rotate(0deg) scale(1);
          }
          33% {
            transform: translateY(-15px) rotate(2deg) scale(1.03);
          }
          66% {
            transform: translateY(-10px) rotate(-1deg) scale(0.98);
          }
        }
        
        @keyframes wave {
          0%, 100% {
            transform: translateX(0) scaleY(1);
          }
          25% {
            transform: translateX(-25%) scaleY(1.15);
          }
          50% {
            transform: translateX(-50%) scaleY(0.9);
          }
          75% {
            transform: translateX(-75%) scaleY(1.1);
          }
        }
        
        @keyframes gradient-x {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes gradient-flow {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        
        @keyframes slide-up-stagger {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fade-in-stagger {
          from {
            opacity: 0;
            transform: translateY(15px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes checkmark {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes success-pulse {
          0%, 100% { 
            transform: scale(1); 
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); 
          }
          50% { 
            transform: scale(1.05); 
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); 
          }
        }
        
        @keyframes width-pulse {
          0%, 100% {
            width: 0;
            left: 50%;
          }
          50% {
            width: 100%;
            left: 0;
          }
        }
        
        @keyframes width-pulse-slow {
          0%, 100% {
            width: 0;
            left: 50%;
          }
          50% {
            width: 60%;
            left: 20%;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.05);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.5;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes card-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        
        @keyframes stat-float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        
        @keyframes button-pulse {
          0%, 100% {
            box-shadow: 0 8px 20px rgba(244, 114, 182, 0.3);
          }
          50% {
            box-shadow: 0 12px 30px rgba(244, 114, 182, 0.5);
          }
        }
        
        /* Animation classes */
        .animate-bubble-float {
          animation: bubble-float 25s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-star-twinkle {
          animation: star-twinkle 18s ease-in-out infinite;
          will-change: transform, opacity, filter;
        }
        
        .animate-heart-float {
          animation: heart-float 20s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-orb-float {
          animation: orb-float 15s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-orb-float-slow {
          animation: orb-float-slow 20s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-wave {
          animation: wave 30s linear infinite;
          will-change: transform;
        }
        
        .animate-gradient-x {
          animation: gradient-x 3s ease infinite;
          background-size: 200% 200%;
          will-change: background-position;
        }
        
        .animate-gradient-flow {
          animation: gradient-flow 3s ease infinite;
          background-size: 200% auto;
          will-change: background-position;
        }
        
        .animate-slide-up-stagger {
          animation: slide-up-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-fade-in-stagger {
          animation: fade-in-stagger 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        
        .animate-scale-in {
          animation: scale-in 0.6s ease-out forwards;
        }
        
        .animate-checkmark {
          animation: checkmark 0.3s ease-out forwards;
        }
        
        .animate-success-pulse {
          animation: success-pulse 2s ease-in-out infinite;
        }
        
        .animate-width-pulse {
          animation: width-pulse 3s ease-in-out infinite;
          will-change: width, left;
        }
        
        .animate-width-pulse-slow {
          animation: width-pulse-slow 4s ease-in-out infinite;
          will-change: width, left;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
          will-change: transform, opacity;
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
          will-change: transform;
        }
        
        .animate-spin-slow {
          animation: spin-slow 30s linear infinite;
          will-change: transform;
        }
        
        .animate-bounce {
          animation: bounce 1s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-card-float {
          animation: card-float 8s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-stat-float {
          animation: stat-float 7s ease-in-out infinite;
          will-change: transform;
        }
        
        .animate-button-pulse {
          animation: button-pulse 2s ease-in-out infinite;
          will-change: box-shadow;
        }
        
        /* Performance optimizations */
        @media (prefers-reduced-motion: reduce) {
          .animate-bubble-float,
          .animate-star-twinkle,
          .animate-heart-float,
          .animate-orb-float,
          .animate-orb-float-slow,
          .animate-wave,
          .animate-gradient-x,
          .animate-gradient-flow,
          .animate-slide-up-stagger,
          .animate-fade-in-stagger,
          .animate-scale-in,
          .animate-checkmark,
          .animate-success-pulse,
          .animate-width-pulse,
          .animate-width-pulse-slow,
          .animate-pulse,
          .animate-pulse-slow,
          .animate-spin,
          .animate-spin-slow,
          .animate-bounce,
          .animate-bounce-slow,
          .animate-card-float,
          .animate-stat-float,
          .animate-button-pulse {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default Contact;